import React, { useState, useEffect, useRef } from 'react';
import api from '../../../services/api';

const SlackAutoImportStep = ({ onSkipToComplete, onBack }) => {
  const [userCount, setUserCount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({ imported: 0, total: 0, failed: 0 });
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    fetchSlackUserCount();
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  const fetchSlackUserCount = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/admin/users/slack-count');
      setUserCount(response.data.count);
    } catch (error) {
      console.error('Error fetching Slack user count:', error);
      setError('Failed to fetch Slack user count');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImportAll = async () => {
    try {
      setIsImporting(true);
      setError(null);

      const response = await api.post('/admin/users/import-from-slack?import_all=true');
      const importId = response.data.import_id;
      setProgress({ imported: 0, total: response.data.total, failed: 0 });

      // Poll for progress
      pollIntervalRef.current = setInterval(async () => {
        try {
          const statusRes = await api.get(`/admin/users/import-from-slack/status/${importId}`);
          setProgress(statusRes.data);

          if (statusRes.data.status === 'completed') {
            clearInterval(pollIntervalRef.current);
            setIsImporting(false);
            onSkipToComplete();
          }
        } catch (err) {
          console.error('Error polling status:', err);
        }
      }, 2000);

    } catch (error) {
      console.error('Error importing users:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to import users. Please try again.';
      setError(errorMessage);
      setIsImporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center">
        <div className="text-gray-600">Loading Slack workspace info...</div>
      </div>
    );
  }

  if (error && !userCount) {
    return (
      <div className="text-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
          <p className="text-red-800">{error}</p>
        </div>
        <button
          onClick={onSkipToComplete}
          className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
        >
          Continue
        </button>
        <p className="text-sm text-gray-500 mt-4">
          You can import users later from the admin dashboard
        </p>
      </div>
    );
  }

  return (
    <div className="text-center">
      <h2 className="text-3xl font-bold text-gray-900 mb-4">Import Your Team</h2>
      <p className="text-lg text-gray-600 mb-8">
        We found <span className="font-bold text-indigo-600">{userCount}</span> user{userCount !== 1 ? 's' : ''} in your Slack workspace
      </p>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
        <p className="text-gray-700 mb-4">
          Would you like to import all of them to Apprecio? This will send each user an email to create their account.
        </p>
        {userCount > 1 && (
          <p className="text-sm text-gray-500">
            Estimated time: {Math.ceil((userCount - 1) * 3 / 60) < 1
              ? `~${(userCount - 1) * 3} seconds`
              : `~${Math.ceil((userCount - 1) * 3 / 60)} minute${Math.ceil((userCount - 1) * 3 / 60) > 1 ? 's' : ''}`}
          </p>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {isImporting && progress.total > 0 && (
        <div className="mb-6">
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${Math.round((progress.imported / progress.total) * 100)}%` }}
            />
          </div>
          <p className="text-sm text-gray-600 mt-2">
            {progress.imported} of {progress.total} users imported...
          </p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-center gap-4">
        {onBack && (
          <button
            onClick={onBack}
            disabled={isImporting}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium disabled:opacity-50"
          >
            Back
          </button>
        )}
        <button
          onClick={handleImportAll}
          disabled={isImporting}
          className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isImporting ? 'Importing...' : 'Import All Users'}
        </button>
        <button
          onClick={onSkipToComplete}
          disabled={isImporting}
          className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium disabled:opacity-50"
        >
          Skip
        </button>
      </div>

      <p className="text-sm text-gray-500 mt-6">
        You can import users manually later from the admin dashboard
      </p>
    </div>
  );
};

export default SlackAutoImportStep;
